class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();

    // Brand pills (sections/orizza-header.liquid) changed — re-run the current search with the new brand.
    // Instances without pills (e.g. the Frost header) have nothing to re-filter.
    document.addEventListener('orizza:brand-changed', () => {
      if (this.searchTerm && this.querySelector('[data-orizza-brand-pills]')) this.getSearchResults(this.searchTerm);
    });
  }

  // Spell correction ("crecent" -> "crescent"). Shopify search only matches real words, so misspelt
  // terms are snapped to the closest word from the store's own product titles, types, tags and vendors.
  // The vocabulary is built once per session from /products.json and shared by every instance.
  static loadVocabulary() {
    if (PredictiveSearch.vocabularyPromise) return PredictiveSearch.vocabularyPromise;

    const storageKey = 'oz-search-vocab-v1';
    try {
      const cached = sessionStorage.getItem(storageKey);
      if (cached) {
        PredictiveSearch.vocabulary = new Set(JSON.parse(cached));
        return (PredictiveSearch.vocabularyPromise = Promise.resolve(PredictiveSearch.vocabulary));
      }
    } catch (e) {}

    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const words = new Set();
    const addWords = (text) =>
      String(text || '')
        .toLowerCase()
        .split(/[^a-z]+/)
        .forEach((word) => word.length >= 3 && words.add(word));

    const loadPage = (page) =>
      fetch(`${root}products.json?limit=250&page=${page}`)
        .then((response) => (response.ok ? response.json() : { products: [] }))
        .then(({ products = [] }) => {
          products.forEach((product) => {
            addWords(product.title);
            addWords(product.product_type);
            addWords(product.vendor);
            (Array.isArray(product.tags) ? product.tags : [product.tags]).forEach(addWords);
          });
          return products.length === 250 && page < 8 ? loadPage(page + 1) : null;
        });

    PredictiveSearch.vocabularyPromise = loadPage(1)
      .then(() => {
        PredictiveSearch.vocabulary = words;
        try {
          sessionStorage.setItem(storageKey, JSON.stringify([...words]));
        } catch (e) {}
        return words;
      })
      .catch(() => (PredictiveSearch.vocabulary = words));
    return PredictiveSearch.vocabularyPromise;
  }

  // Damerau-Levenshtein (optimal string alignment) distance, so swapped letters count as one typo
  static editDistance(a, b) {
    const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
    for (let j = 1; j <= b.length; j++) d[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        }
      }
    }
    return d[a.length][b.length];
  }

  correctWord(word) {
    const vocabulary = PredictiveSearch.vocabulary;
    if (!vocabulary || word.length < 4 || !/^[a-z]+$/.test(word) || vocabulary.has(word)) return word;
    // Plural / singular of a known word ("hoops", "ring")
    if (vocabulary.has(word.replace(/e?s$/, '')) || vocabulary.has(`${word}s`)) return word;

    // Still typing a real word ("cresc") — leave it alone
    for (const candidate of vocabulary) if (candidate.startsWith(word)) return word;

    const maxTypos = word.length <= 5 ? 1 : 2;
    let best = word;
    let bestDistance = maxTypos + 1;
    for (const candidate of vocabulary) {
      if (Math.abs(candidate.length - word.length) > maxTypos + 2) continue;
      // Also compare against the start of longer words, for a misspelt word that's only half typed ("crecen")
      const distance = Math.min(
        PredictiveSearch.editDistance(word, candidate),
        candidate.length > word.length ? PredictiveSearch.editDistance(word, candidate.slice(0, word.length + 1)) : Infinity
      );
      // On a tie, prefer the word closest in length ("ernings" -> "earrings", not "rings")
      const closerLength = Math.abs(candidate.length - word.length) < Math.abs(best.length - word.length);
      if (distance < bestDistance || (distance === bestDistance && closerLength)) {
        best = candidate;
        bestDistance = distance;
      }
    }
    return bestDistance <= maxTypos ? best : word;
  }

  correctQuery(query) {
    return query.replace(/[A-Za-z]+/g, (word) => {
      const corrected = this.correctWord(word.toLowerCase());
      return corrected === word.toLowerCase() ? word : corrected;
    });
  }

  // Shopify builds query suggestions partly from what shoppers have searched before, so misspellings
  // like "crecent ring" show up there and can't be removed from the admin. Correct each suggestion
  // (text, highlight and link), then drop any that become duplicates.
  correctSuggestions(searchTerm) {
    const list = this.predictiveSearchResults.querySelector('#predictive-search-results-queries-list');
    if (!list || !PredictiveSearch.vocabulary) return;

    const seen = new Set();
    list.querySelectorAll('.predictive-search__item-query-result').forEach((heading) => {
      const item = heading.closest('li');
      const text = heading.getAttribute('aria-label') || heading.textContent.trim();
      const corrected = this.correctQuery(text).toLowerCase();

      if (seen.has(corrected)) {
        item.remove();
        return;
      }
      seen.add(corrected);
      if (corrected === text.toLowerCase()) return;

      heading.setAttribute('aria-label', corrected);
      // Rebuild Shopify's styling: the typed part in <mark>, the rest in <span>
      const start = corrected.indexOf(searchTerm.toLowerCase());
      const parts =
        start === -1
          ? [['span', corrected]]
          : [
              ['span', corrected.slice(0, start)],
              ['mark', corrected.slice(start, start + searchTerm.length)],
              ['span', corrected.slice(start + searchTerm.length)],
            ];
      heading.replaceChildren(
        ...parts
          .filter(([, part]) => part)
          .map(([tag, part]) => Object.assign(document.createElement(tag), { textContent: part }))
      );

      const link = item.querySelector('a');
      if (link) {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('q', corrected);
        link.href = url.pathname + url.search;
      }
    });

    // Re-number ids so keyboard navigation (aria-activedescendant) stays valid
    list.querySelectorAll('li[id^="predictive-search-option-query-"]').forEach((li, index) => {
      li.id = `predictive-search-option-query-${index + 1}`;
    });
  }

  getActiveBrand() {
    return this.querySelector('[data-orizza-brand-pills] [aria-pressed="true"]')?.dataset.brand || '';
  }

  // The predictive search API ignores vendor:"X" filters, so brand filtering happens on the rendered markup:
  // product rows carry data-vendor (sections/predictive-search.liquid) and non-matching ones are hidden.
  // Collections, pages, articles and query suggestions are never touched.
  applyBrandFilter() {
    const brand = this.getActiveBrand().trim().toLowerCase();
    const products = this.predictiveSearchResults.querySelectorAll('#predictive-search-results-products-list [data-vendor]');
    if (!products.length) return;

    let visibleCount = 0;
    products.forEach((item) => {
      const show = !brand || item.dataset.vendor.trim().toLowerCase() === brand;
      item.style.display = show ? '' : 'none';
      if (show) visibleCount++;
    });

    // Mirror what the section renders natively when there are no products, so the layout doesn't leave an empty column
    const productsGroup = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list').parentElement;
    productsGroup.style.display = visibleCount ? '' : 'none';

    const wrapper = this.predictiveSearchResults.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrapper) return;
    wrapper.classList.toggle('predictive-search__results-groups-wrapper--no-products', !visibleCount);
    const hasOtherResults = wrapper.querySelector('li:not([data-vendor])');
    wrapper.style.display = visibleCount || hasOtherResults ? '' : 'none';
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    // Send the full search page the corrected spelling too
    const corrected = this.correctQuery(this.getQuery());
    if (corrected !== this.getQuery()) this.input.value = corrected;
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    if (!PredictiveSearch.vocabulary) {
      // Once the vocabulary arrives, redo a search that went out uncorrected
      PredictiveSearch.loadVocabulary().then(() => {
        if (this.searchTerm) this.getSearchResults(this.searchTerm);
      });
    }

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(typedTerm) {
    const searchTerm = this.correctQuery(typedTerm);
    const correctedTerm = searchTerm !== typedTerm ? searchTerm : null;
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], correctedTerm, searchTerm);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        // A newer keystroke may have changed the term while this was loading
        if (this.correctQuery(this.searchTerm) !== searchTerm) {
          searchDeferred?.resolve({ totalCount: 0 });
          return;
        }
        this.renderSearchResults(resultsMarkup, correctedTerm, searchTerm);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, correctedTerm = null, searchTerm = this.searchTerm) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.correctSuggestions(searchTerm);
    if (correctedTerm) {
      const note = document.createElement('p');
      note.className = 'predictive-search__correction';
      note.append('Showing results for ', Object.assign(document.createElement('strong'), { textContent: correctedTerm }));
      this.predictiveSearchResults.prepend(note);
    }
    this.applyBrandFilter();
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
